import React, { useState, useEffect, createContext, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = () => {
    try {
      const token = localStorage.getItem('authToken');
      const userData = localStorage.getItem('userData');
      
      if (token && userData) {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
        setIsAuthenticated(true);
      } else {
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error('Error checking auth status:', error);
      logout();
    } finally {
      setIsLoading(false);
    }
  };

  const login = (userData, token) => {
    localStorage.setItem('authToken', token);
    localStorage.setItem('userData', JSON.stringify(userData));
    setUser(userData);
    setIsAuthenticated(true);
    
    // Dispatch storage event for other components
    window.dispatchEvent(new Event('storage'));
  };

  const logout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');
    setUser(null);
    setIsAuthenticated(false);
    
    // Dispatch storage event for other components
    window.dispatchEvent(new Event('storage'));
    
    navigate('/login');
  };

  const updateUser = (updatedUserData) => {
    const newUserData = { ...user, ...updatedUserData };
    localStorage.setItem('userData', JSON.stringify(newUserData));
    setUser(newUserData);
    
    // Dispatch storage event for other components
    window.dispatchEvent(new Event('storage'));
  };

  const value = {
    isAuthenticated,
    user,
    isLoading,
    login,
    logout,
    updateUser,
    checkAuthStatus
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

const AuthenticationGuard = ({ 
  children, 
  requireAuth = true, 
  redirectTo = '/login',
  allowedRoles = [],
  fallback = null 
}) => {
  const { isAuthenticated, user, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (isLoading) return;

    if (requireAuth && !isAuthenticated) {
      // Save the intended destination
      const intendedPath = location?.pathname + location?.search;
      localStorage.setItem('intendedPath', intendedPath);
      navigate(redirectTo);
      return;
    }

    if (!requireAuth && isAuthenticated) {
      // Redirect authenticated users away from auth pages
      const intendedPath = localStorage.getItem('intendedPath');
      if (intendedPath) {
        localStorage.removeItem('intendedPath');
        navigate(intendedPath);
      } else {
        navigate('/homepage-dashboard');
      }
      return;
    }

    if (requireAuth && isAuthenticated && allowedRoles?.length > 0) {
      const userRole = user?.role || 'user';
      if (!allowedRoles?.includes(userRole)) {
        navigate('/homepage-dashboard');
        return;
      }
    }
  }, [isAuthenticated, user, isLoading, requireAuth, allowedRoles, navigate, location, redirectTo]);

  if (isLoading) {
    return fallback || (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (requireAuth && !isAuthenticated) {
    return null;
  }

  if (!requireAuth && isAuthenticated) {
    return null;
  }

  if (requireAuth && isAuthenticated && allowedRoles?.length > 0) {
    const userRole = user?.role || 'user';
    if (!allowedRoles?.includes(userRole)) {
      return null;
    }
  }

  return children;
};

export default AuthenticationGuard;